// MailTemplatePage.tsx

import React, { useEffect, useState } from "react";
import CustomMenu from "../../components/CustomMenu/CustomMenu";
import { Box, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import { axiosRequest } from "../../service/AxiosService";
import { EditMailTemplateInitResponse } from "../../type/response/EditMailTemplateInitResponse";
import {
  EditMailTemplateRequest,
  ToUser,
} from "../../type/request/EditMailTemplateRequest";
import EditMailTemplateDialog from "./component/EditMailTemplateDialog";
import { StyledBox } from "./component/MailBodyComponent";
import CustomTableComponent, {
  StyledTableCell,
} from "./component/CustomTableComponent";

/** テーブルの列定義 */
const columnLabels: Partial<Record<keyof EditMailTemplateRequest, string>> = {
  name: "テンプレート名",
  description: "説明",
  toUsers: "宛先",
  subject: "件名",
  body: "本文(プレビュー)",
};

/**
 * 宛先リストを文字列として返す
 * @param toUsers
 * @returns
 */
const getToUsersStr = (toUsers?: ToUser[]) =>
  `${toUsers ? toUsers.map((v) => `${v.lastName}${v.middleName ?? ""}${v.firstName}`).join(",") : ""}`;

/**
 * メールテンプレートデータを文字列として取得
 * @param data
 * @returns
 */
const getDataStr = (data: EditMailTemplateRequest) =>
  ({
    id: data.id,
    name: data.name,
    description: data.description,
    subject: data.subject,
    body: data.body,
    toUsers: getToUsersStr(data.toUsers),
  }) as Partial<Record<keyof EditMailTemplateRequest, string>>;

/**
 * メールテンプレート一覧ページ
 * @returns
 */
export default function MailTemplatePage() {
  const [rows, setRows] = useState<EditMailTemplateRequest[]>([]);
  const [selectToUsers, setSelectToUsers] = useState<ToUser[]>([]);
  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    data?: EditMailTemplateRequest;
  }>({ open: false });

  /** 初期処理 */
  const initialize = async () => {
    try {
      const response = await axiosRequest<EditMailTemplateInitResponse>(
        "/mail/initialize",
        "GET"
      );
      if (response) {
        setRows(response.allMailTemplate);
        setSelectToUsers(response.selectToUsers);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  return (
    <CustomMenu>
      <CustomTableComponent
        name="EditMailTemplate"
        rows={rows}
        columnLabels={columnLabels}
        getDataStr={getDataStr}
        customButtonCellRenderer={() => (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setEditDialog({ open: true })}
          >
            メールテンプレート新規作成
          </Button>
        )}
        handleClickRow={(row) =>
          setEditDialog({ open: true, data: row as EditMailTemplateRequest })
        }
        cellRenderer={(r, k) => {
          const row = r as EditMailTemplateRequest;
          const key = k as keyof EditMailTemplateRequest;
          const render = () => {
            switch (key) {
              // case "code":
              case "name":
                return <Box width={140}>{row[key]}</Box>;
              case "description":
                return <Box width={200}>{row.description}</Box>;
              case "toUsers":
                return <Box width={170}>{getToUsersStr(row.toUsers)}</Box>;
              case "subject":
                return <Box width={400}>{row[key]}</Box>;
              case "body":
                return (
                  <StyledBox
                    width={600}
                    height={300}
                    overflow="auto"
                    sx={{ p: 1, resize: "both" }}
                    dangerouslySetInnerHTML={{ __html: row.body }}
                  />
                );
              default:
                return <></>;
            }
          };
          return (
            <StyledTableCell key={key} align="left" sx={{ px: 1 }}>
              {render()}
            </StyledTableCell>
          );
        }}
      />
      <EditMailTemplateDialog
        open={editDialog.open}
        onClose={() => {
          setEditDialog({ open: false });
          initialize();
        }}
        data={editDialog.data}
        allMailTemplate={rows}
        selectToUsers={selectToUsers}
      />
    </CustomMenu>
  );
}
